import { useEffect, useMemo, useState } from 'react';
import type {
  FrameworkDetail,
  FrameworkModuleType,
  FrameworkSkeletonKind,
  FrameworkSummary,
} from '../../api/frameworks.js';
import { useApi } from '../../api/client.js';
import { useFrameworks } from '../../api/frameworks.js';

interface Props {
  moduleType: FrameworkModuleType;
  /** Optional: restrict the card list to one skeleton kind. Widget-mode
   *  dialogs show only widget frameworks; HTML-mode dialogs show only
   *  html frameworks. Omit to show everything. */
  skeletonKind?: FrameworkSkeletonKind;
  /** Picked framework ID (or null = "start from scratch"). Controlled. */
  value: string | null;
  onChange(id: string | null): void;
  /** Optional: when the full skeleton becomes available (after user clicks
   *  a card), the parent gets it so it can preview / seed v1 from it. */
  onSkeletonLoaded?(detail: FrameworkDetail): void;
}

/**
 * Grid of framework cards shown at the top of "New template" /
 * "New dashboard" dialogs. Each card shows the name, module type, and a
 * short outline preview. Picking a card fetches the full skeleton and
 * emits it through onSkeletonLoaded so the parent can seed the first
 * version.
 *
 * The first option is always "Start from scratch" — users can still get
 * a blank template.
 */
export function FrameworkPicker({
  moduleType,
  skeletonKind,
  value,
  onChange,
  onSkeletonLoaded,
}: Props): JSX.Element {
  const { data: rawFrameworks, isLoading } = useFrameworks(moduleType);
  const frameworks = useMemo(
    () =>
      skeletonKind ? rawFrameworks?.filter((f) => f.skeletonKind === skeletonKind) : rawFrameworks,
    [rawFrameworks, skeletonKind],
  );
  const api = useApi();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // When the picked framework changes, fetch its skeleton.
  useEffect(() => {
    if (!value) return;
    let cancelled = false;
    setLoadingId(value);
    setLoadError(null);
    api
      .fetch<FrameworkDetail>(`/frameworks/${value}`)
      .then((detail) => {
        if (cancelled) return;
        setLoadingId(null);
        onSkeletonLoaded?.(detail);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadingId(null);
        setLoadError((err as Error).message || 'Failed to load framework');
      });
    return () => {
      cancelled = true;
    };
    // api intentionally not a dep: useApi is stable-per-session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="framework-picker">
      <div className="framework-picker-label">
        Start from a framework
        <span className="framework-picker-hint">
          · pick one to seed a v1, or skip to start blank
        </span>
      </div>

      {isLoading ? (
        <div className="text-dim text-sm" style={{ padding: 'var(--space-3) 0' }}>
          Loading frameworks…
        </div>
      ) : (
        <div className="framework-grid">
          <button
            type="button"
            className={
              'framework-card framework-card--scratch' +
              (value === null ? ' framework-card--picked' : '')
            }
            onClick={() => onChange(null)}
          >
            <div className="framework-card-title">Start from scratch</div>
            <div className="framework-card-desc">
              Blank template — you tell Copilot what to build.
            </div>
          </button>

          {frameworks?.map((fw) => (
            <FrameworkCard
              key={fw.id}
              fw={fw}
              picked={value === fw.id}
              loading={loadingId === fw.id}
              onPick={() => onChange(fw.id)}
            />
          ))}
        </div>
      )}

      {loadError && (
        <div className="alert alert--danger" style={{ marginTop: 'var(--space-2)' }}>
          {loadError}
        </div>
      )}
    </div>
  );
}

function FrameworkCard({
  fw,
  picked,
  loading,
  onPick,
}: {
  fw: FrameworkSummary;
  picked: boolean;
  loading: boolean;
  onPick(): void;
}): JSX.Element {
  const outlinePreview = fw.outline.slice(0, 3);
  const remainder = Math.max(0, fw.outline.length - outlinePreview.length);
  const desc = describeFramework(fw);

  return (
    <button
      type="button"
      className={'framework-card' + (picked ? ' framework-card--picked' : '')}
      onClick={onPick}
      disabled={loading}
    >
      <div className="framework-card-head">
        <div className="framework-card-title">{fw.name}</div>
        <span className="framework-card-badge">{labelForModule(fw.moduleType)}</span>
      </div>
      {desc && <div className="framework-card-desc">{desc}</div>}
      {outlinePreview.length > 0 && (
        <ul className="framework-card-outline">
          {outlinePreview.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
          {remainder > 0 && <li className="framework-card-outline-more">+{remainder} more…</li>}
        </ul>
      )}
      {loading && <div className="framework-card-loading">Loading…</div>}
    </button>
  );
}

function labelForModule(m: FrameworkModuleType): string {
  switch (m) {
    case 'report':
      return 'Report';
    case 'dashboard':
      return 'Dashboard';
    case 'either':
      return 'Any';
  }
}

function describeFramework(fw: FrameworkSummary): string {
  // Pull a short description from guidance.style or guidance.datasets if
  // the shipped framework's guidance blob provides either.
  const g = fw.guidance as { style?: string; datasets?: string };
  if (g?.style) return g.style;
  if (g?.datasets) return `Datasets: ${g.datasets}`;
  return '';
}
